/**
 * Deterministic trigonometry. Math.sin/cos are implementation-approximated and
 * differ across JS engines; this file uses only +, -, *, / on doubles, which
 * IEEE-754 pins down, so every engine produces the same bits.
 */
import { YAW_STEPS } from "../input.ts";

export const PI = Math.PI;
export const TWO_PI = Math.PI * 2;
const HALF_PI = Math.PI / 2;

/** sin(x) for x in [-π/2, π/2] via a degree-15 Taylor series (error < 1e-9 there). */
function sinCore(x: number): number {
  const x2 = x * x;
  // Horner form of x - x^3/3! + x^5/5! - ... + x^15/15!
  let p = -1 / 1307674368000; // 15!
  p = p * x2 + 1 / 6227020800; // 13!
  p = p * x2 - 1 / 39916800; // 11!
  p = p * x2 + 1 / 362880; // 9!
  p = p * x2 - 1 / 5040; // 7!
  p = p * x2 + 1 / 120; // 5!
  p = p * x2 - 1 / 6; // 3!
  p = p * x2 + 1;
  return p * x;
}

/** Reduce any angle to [-π, π]. */
export function wrapAngle(a: number): number {
  let x = a - TWO_PI * Math.floor((a + PI) / TWO_PI);
  if (x < -PI) x += TWO_PI;
  if (x > PI) x -= TWO_PI;
  return x;
}

export function sin(a: number): number {
  let x = wrapAngle(a);
  // Fold [-π, π] onto [-π/2, π/2]: sin(π - x) = sin(x).
  if (x > HALF_PI) x = PI - x;
  else if (x < -HALF_PI) x = -PI - x;
  return sinCore(x);
}

export function cos(a: number): number {
  return sin(a + HALF_PI);
}

/** Yaw steps (0..65535) → radians. */
export function yawToRadians(yaw: number): number {
  return (yaw / YAW_STEPS) * TWO_PI;
}

/** Unit vector on the ground plane for a yaw (x right, z forward at yaw 0). */
export function yawDirection(yaw: number): { x: number; z: number } {
  const a = yawToRadians(yaw);
  return { x: sin(a), z: cos(a) };
}

/** atan2 with only rational ops; Abramowitz & Stegun 4.4.49, |error| ≤ 1e-8 rad. */
export function atan2(y: number, x: number): number {
  if (x === 0 && y === 0) return 0;
  const ax = Math.abs(x);
  const ay = Math.abs(y);
  const a = ax > ay ? ay / ax : ax / ay; // in [0, 1]
  const s = a * a;
  let p = -0.004054058;
  p = p * s + 0.0218612288;
  p = p * s - 0.0559098861;
  p = p * s + 0.0964200441;
  p = p * s - 0.1390853351;
  p = p * s + 0.1994653599;
  p = p * s - 0.3332985605;
  p = p * s + 0.9999993329;
  let r = p * a;
  if (ay > ax) r = HALF_PI - r;
  if (x < 0) r = PI - r;
  if (y < 0) r = -r;
  return r;
}
